import { useState, useRef } from 'react'
import { Send, Loader2 } from 'lucide-react'
import { API_URL } from '../services/authService'
import { Turnstile } from './Turnstile'
import { trackEvent } from '../src/lib/googleAnalytics'

interface NewsletterWidgetProps {
  /** 'onDark' = widget sur fond coloré (bleu), 'onLight' = sur fond blanc/gris */
  variant?: 'onDark' | 'onLight'
  onSuccess?: () => void
  className?: string
}

export function NewsletterWidget({ variant = 'onLight', onSuccess, className = '' }: NewsletterWidgetProps) {
  const [email, setEmail] = useState('')
  const [isSubscribing, setIsSubscribing] = useState(false)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [turnstileToken, setTurnstileToken] = useState('')
  const honeypotRef = useRef<HTMLInputElement>(null)

  const isDark = variant === 'onDark'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubscribing(true)
    setFeedback(null)
    try {
      const res = await fetch(`${API_URL}/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          _hp: honeypotRef.current?.value ?? '',
          cf_turnstile_response: turnstileToken,
        }),
      })
      if (res.ok) {
        trackEvent('sign_up', { method: 'newsletter' })
        setFeedback({ type: 'success', message: 'Inscription confirmée ! Vous recevrez nos prochains articles.' })
        setEmail('')
        setTurnstileToken('')
        localStorage.setItem('nl_subscribed', '1')
        onSuccess?.()
      } else {
        const data = await res.json().catch(() => ({}))
        setFeedback({ type: 'error', message: data.error ?? "Erreur lors de l'inscription." })
      }
    } catch {
      setFeedback({ type: 'error', message: 'Erreur de connexion. Veuillez réessayer.' })
    } finally {
      setIsSubscribing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${className}`}>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          aria-label="Votre adresse email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Votre adresse email"
          required
          className={`flex-1 px-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 ${
            isDark
              ? 'bg-white text-gray-900 placeholder-gray-500 focus:ring-white'
              : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500'
          }`}
        />
        <button
          type="submit"
          disabled={isSubscribing}
          className={`px-5 py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap ${
            isDark
              ? 'bg-white text-jepg-blue hover:bg-gray-100'
              : 'bg-jepg-blue text-white hover:bg-jepg-blue-hover'
          }`}
        >
          {isSubscribing ? (
            <><Loader2 size={16} className="animate-spin" /> Inscription...</>
          ) : (
            <><Send size={16} /> S'inscrire</>
          )}
        </button>
      </div>

      {/* Honeypot */}
      <div className="absolute -left-[9999px] h-px overflow-hidden" aria-hidden="true">
        <input type="text" name="_hp" ref={honeypotRef} tabIndex={-1} autoComplete="off" />
      </div>

      <Turnstile onVerify={setTurnstileToken} className="flex justify-start" />

      {feedback && (
        <p className={`text-sm ${
          feedback.type === 'success'
            ? isDark ? 'text-green-300' : 'text-green-600'
            : isDark ? 'text-red-300' : 'text-red-600'
        }`}>
          {feedback.message}
        </p>
      )}

      <p className={`text-xs ${isDark ? 'text-blue-200' : 'text-gray-400'}`}>
        Vie privée respectée. Désinscription possible à tout moment.
      </p>
    </form>
  )
}
